import { useEffect, useState } from 'react';
import { periodLocksApi } from '../../lib/api';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Field, TextInput } from '../../components/Field';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Can } from '../../lib/permissions';
import { useShortcuts } from '../../lib/keys';
import type { PeriodLock } from '../../lib/types';

export function PeriodLocksPage() {
  const [locks, setLocks] = useState<PeriodLock[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [lockedFrom, setLockedFrom] = useState('');
  const [lockedTo, setLockedTo] = useState('');
  const [reason, setReason] = useState('');

  function load() {
    periodLocksApi.list().then(({ items }) => setLocks(items)).catch(setError);
  }

  useEffect(load, []);

  useShortcuts('Period locks', [
    { id: 'new-lock', bindings: [{ key: 'n', alt: true }], description: 'New period lock', handler: () => setModalOpen(true) },
  ]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await periodLocksApi.create({ lockedFrom, lockedTo, reason: reason || null });
      setModalOpen(false);
      setLockedFrom('');
      setLockedTo('');
      setReason('');
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(lock: PeriodLock) {
    try {
      await periodLocksApi.delete(lock.id);
      load();
    } catch (err) {
      setError(err);
    }
  }

  const columns: DataTableColumn<PeriodLock>[] = [
    { key: 'range', header: 'Period', render: (l) => `${l.lockedFrom} → ${l.lockedTo}` },
    { key: 'reason', header: 'Reason', render: (l) => l.reason ?? '—' },
    {
      key: 'actions',
      header: '',
      render: (l) => (
        <Can permission="company:period_lock:delete">
          <button onClick={() => handleDelete(l)} className="rounded-[3px] border border-[var(--border)] px-1.5 py-0.5 text-[11px] hover:bg-[var(--bg-hover)]">
            Unlock
          </button>
        </Can>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">Period locks</h1>
        <Can permission="company:period_lock:create">
          <button onClick={() => setModalOpen(true)} className="rounded-[3px] border border-[var(--border)] px-2 py-1 text-[12px] hover:bg-[var(--bg-hover)]">
            New lock <span className="kbd ml-1">Alt+N</span>
          </button>
        </Can>
      </div>
      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}
      <DataTable columns={columns} rows={locks} getRowKey={(l) => l.id} emptyMessage="No periods locked." />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="New period lock">
        <form onSubmit={handleCreate} className="flex flex-col gap-3">
          <Field label="Starts on" htmlFor="l-start" required>
            <TextInput id="l-start" type="date" autoFocus value={lockedFrom} onChange={(e) => setLockedFrom(e.target.value)} required />
          </Field>
          <Field label="Ends on" htmlFor="l-end" required>
            <TextInput id="l-end" type="date" value={lockedTo} onChange={(e) => setLockedTo(e.target.value)} required />
          </Field>
          <Field label="Reason" htmlFor="l-reason" hint="Shown to anyone who hits this lock while entering a voucher.">
            <TextInput id="l-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="flex justify-end">
            <button type="submit" disabled={busy} className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50">
              {busy ? 'Saving…' : 'Lock'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
